import React, { useEffect, useMemo, useState } from 'react';
import { Form } from 'react-bootstrap';
import Select from 'react-select';
import { toast } from 'react-toastify';
import axiosInstance from '../api/axios';

// ─── Styles ────────────────────────────────────────────────────────────────────

const selectStyles = {
    control: (provided, state) => ({
        ...provided,
        borderColor: state.isFocused ? '#86b7fe' : '#c5cbd1',
        minHeight: '38px',
        boxShadow: state.isFocused ? '0 0 0 0.25rem rgba(13,110,253,.25)' : 'none',
        '&:hover': { borderColor: '#6c757d' },
    }),
    groupHeading: (provided) => ({
        ...provided,
        fontSize: '0.7rem',
        fontWeight: '700',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        padding: '6px 12px 4px',
        color: '#6c757d',
        borderBottom: '1px solid #e9ecef',
        marginBottom: '4px',
    }),
    option: (provided, state) => ({
        ...provided,
        fontSize: '0.875rem',
        backgroundColor: state.isSelected ? '#0d6efd' : state.isFocused ? '#e8f0fe' : 'white',
        color: state.isSelected ? 'white' : '#212529',
        cursor: 'pointer',
    }),
    singleValue: (provided, state) => ({
        ...provided,
        color: state.data?.status !== 'ACTIVE' ? '#dc3545' : '#212529',
    }),
};

// ─── Option Label ──────────────────────────────────────────────────────────────

const formatOptionLabel = ({ label, status }) => (
    <div className="d-flex align-items-center justify-content-between">
        <span>{label}</span>
        {status && status !== 'ACTIVE' && (
            <span
                style={{
                    fontSize: '0.65rem',
                    fontWeight: '600',
                    padding: '1px 7px',
                    borderRadius: '10px',
                    backgroundColor: status === 'INACTIVE' ? '#dc354520' : '#fd7e1420',
                    color: status === 'INACTIVE' ? '#dc3545' : '#fd7e14',
                    border: `1px solid ${status === 'INACTIVE' ? '#dc354540' : '#fd7e1440'}`,
                    marginLeft: '8px',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                }}
            >
                {status === 'INACTIVE' ? 'Inactive' : 'Coming Soon'}
            </span>
        )}
    </div>
);

// ─── Component ─────────────────────────────────────────────────────────────────

/**
 * OutletSelect — Reusable outlet dropdown with grouped status options.
 *
 * Props:
 *  @param {object|null}   value           - Currently selected option ({ value, label, status })
 *  @param {function}      onChange         - Called with the selected option (or null when cleared)
 *  @param {string}        [label]          - Form label text. Default: "Outlet"
 *  @param {string}        [placeholder]    - Placeholder text. Default: "Select Outlet..."
 *  @param {boolean}       [required]       - Marks the field as required. Default: false
 *  @param {boolean}       [isClearable]    - Allow clearing selection. Default: true
 *  @param {boolean}       [isDisabled]     - Disable the select. Default: false
 *  @param {boolean}       [showLabel]      - Show the form label. Default: true
 *  @param {object[]}      [outlets]        - Pre-fetched outlets array. If omitted, component fetches internally.
 *  @param {boolean}       [isLoading]      - External loading state (used when outlets prop is provided).
 *  @param {object}        [extraStyles]    - Additional react-select style overrides merged with defaults.
 *  @param {string}        [controlId]      - Form.Group controlId. Default: "outletSelect"
 */
const OutletSelect = ({
    value,
    onChange,
    label = 'Select Outlet',
    placeholder = 'Select Outlet...',
    required = false,
    isClearable = true,
    isDisabled = false,
    showLabel = true,
    outlets: outletsProp,
    isLoading: isLoadingProp = false,
    extraStyles = {},
    controlId = 'outletSelect',
    // ADD THIS 
    isMulti = false,
}) => {
    const [internalOutlets, setInternalOutlets] = useState([]);
    const [internalLoading, setInternalLoading] = useState(false);

    const accessToken = localStorage.getItem('authToken');

    // If outlets are not passed in as a prop, fetch them internally
    const isSelfFetching = outletsProp === undefined;

    useEffect(() => {
        if (!isSelfFetching) return;
        const fetchOutlets = async () => {
            setInternalLoading(true);
            try {
                const headers = { Authorization: `Bearer ${accessToken}` };
                const response = await axiosInstance.get('outlet', { headers });
                if (response?.data?.status === true) {
                    setInternalOutlets(response.data.data.outlets);
                }
            } catch (error) {
                if (error?.response) {
                    const status = error?.response?.status;
                    let errorMessage = status === 406
                        ? error?.response?.data?.error?.message || error?.response?.data?.error
                        : status === 412
                            ? error?.response?.data?.data?.message
                            : error?.response?.data?.error || 'Failed to fetch outlets';
                    toast.error(errorMessage, { position: 'top-right', autoClose: 3000, theme: 'colored' });
                }
            } finally {
                setInternalLoading(false);
            }
        };
        fetchOutlets();
    }, [isSelfFetching]);

    const outlets = isSelfFetching ? internalOutlets : (outletsProp ?? []);
    const loading = isSelfFetching ? internalLoading : isLoadingProp;

    // Build grouped options: Active → Inactive → Coming Soon
    const outletOptions = useMemo(() => {
        const map = (list) =>
            list.map((o) => ({ value: o._id, label: `${o.name} (${o.city})`, status: o.status }));

        const active = map(outlets.filter((o) => o.status === 'ACTIVE'));
        const inactive = map(outlets.filter((o) => o.status === 'INACTIVE'));
        const comingSoon = map(outlets.filter((o) => o.status === 'COMING_SOON'));

        const groups = [];
        if (active.length) groups.push({ label: 'Active Outlets', options: active });
        if (inactive.length) groups.push({ label: 'Inactive Outlets', options: inactive });
        if (comingSoon.length) groups.push({ label: 'Coming Soon', options: comingSoon });

        return groups;
    }, [outlets]);

    const mergedStyles = {
        ...selectStyles,
        ...extraStyles,
        control: (provided, state) => ({
            ...selectStyles.control(provided, state),
            ...(extraStyles.control ? extraStyles.control(provided, state) : {}),
        }),
    };

    return (
        <Form.Group controlId={controlId}>
            {showLabel && (
                <Form.Label className="fw-semibold">
                    <i className="bi bi-shop me-2"></i>{label}
                    {required && <span className="text-danger ms-1">*</span>}
                </Form.Label>
            )}
            <Select
                isClearable={isClearable}
                isSearchable
                isDisabled={isDisabled}
                isLoading={loading}
                isMulti={isMulti}
                placeholder={placeholder}
                value={value}
                onChange={onChange}
                options={outletOptions}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                closeMenuOnSelect={!isMulti ? true : false}
                styles={mergedStyles}
                formatOptionLabel={formatOptionLabel}
                noOptionsMessage={() => (loading ? 'Loading outlets…' : 'No outlets found')}
            />
            {/* Warning for non-active selection */}
            {/* {value && value.status !== 'ACTIVE' && (
                <Form.Text className={value.status === 'INACTIVE' ? 'text-danger' : 'text-warning'}>
                    <i className="bi bi-exclamation-circle me-1"></i>
                    {value.status === 'INACTIVE'
                        ? 'This outlet is currently inactive.'
                        : 'This outlet is coming soon and may not have data yet.'}
                </Form.Text>
            )} */}
            {/* Warning for non-active selection — works for both single and multi */}
            {value && !isMulti && value.status !== 'ACTIVE' && (
                <Form.Text className={value.status === 'INACTIVE' ? 'text-danger' : 'text-warning'}>
                    <i className="bi bi-exclamation-circle me-1"></i>
                    {value.status === 'INACTIVE'
                        ? 'This outlet is currently inactive.'
                        : 'This outlet is coming soon and may not have data yet.'}
                </Form.Text>
            )}
            {value && isMulti && Array.isArray(value) && value.some(v => v.status !== 'ACTIVE') && (
                <Form.Text className="text-warning">
                    <i className="bi bi-exclamation-circle me-1"></i>
                    Some selected outlets are inactive or coming soon.
                </Form.Text>
            )}
        </Form.Group>
    );
};

export default OutletSelect;